import React, { useEffect, useImperativeHandle, useState } from 'react'

import { IQuestionComponentProps, IQuestionComponentRef, QuestionViewFrame } from '../types'
import { IFeedbackResponseValidation, FeedbackQuestionModelTypes, FeedbackResponse } from './types'

import useSession from '../../session/useSession'
import useSurveyLocale from '../../modules/locale/useSurveyLocale'
import QuestionFrame from '../_common/QuestionFrame/QuestionFrame'
import FeedbackView from './View'

const FeedbackQuestion = React.memo(
  React.forwardRef<IQuestionComponentRef, IQuestionComponentProps<FeedbackQuestionModelTypes>>(({ model }, ref) => {
    const [[response, responseValidation, frame], setResponse] = useState<
      [FeedbackResponse, IFeedbackResponseValidation, QuestionViewFrame]
    >([model.response, model.responseValidation, model.frame])

    const session = useSession()
    const { t } = useSurveyLocale()
    const [showValidation, setShowValidation] = useState(false)

    useEffect(() => {
      session.enableNext(responseValidation.valid)
    }, [session, responseValidation.valid])

    const onUpdateResponse = (response: FeedbackResponse) => {
      model.response = response
      setResponse([model.response, model.responseValidation, model.frame])
    }

    useImperativeHandle<IQuestionComponentRef, IQuestionComponentRef>(ref, () => {
      return {
        tryNext() {
          if (model.responseValidation.valid || session.skipValidation) {
            return false
          } else {
            session.enableNext(false)
            return true
          }
        },
        tryPrev() {
          return false
        },
        showValidation() {
          setShowValidation(true)
        }
      }
    }, [model, session])

    return (
      <QuestionFrame model={model} titleText={t('questions', 'feedback', 'title')}>
        <FeedbackView
          schema={model.schema}
          response={response}
          responseValidation={responseValidation}
          showValidation={showValidation}
          onUpdateResponse={onUpdateResponse}
          frame={frame}
        />
      </QuestionFrame>
    )
  }),
  (prevProps, nextProps) => prevProps.model === nextProps.model
)

FeedbackQuestion.displayName = 'FeedbackQuestion'

export default FeedbackQuestion
